import { createFileRoute } from "@tanstack/react-router";
import { SpokePage, buildSpokeHead, type SpokePageConfig } from "@/components/SpokePage";

const CONFIG: SpokePageConfig = {
  slug: "junk-removal-web-design-colorado",
  metaTitle: "Junk Removal Web Design in Colorado | Websites That Book Jobs",
  metaDescription:
    "Custom junk removal web design across Colorado — fast, mobile-first, conversion-tested sites built to turn local searches into booked work. Owner-led. Call (719) 419-3935.",
  breadcrumbs: [
    { label: "Home", to: "/" },
    { label: "Colorado Springs digital marketing", to: "/colorado-springs" },
  ],
  currentLabel: "Junk Removal Web Design Colorado",
  eyebrow: "Junk Removal Web Design — Colorado",
  h1: (
    <>
      Junk Removal <span className="text-brand-gold">Web Design</span> in Colorado
    </>
  ),
  heroDescription:
    "Hand-coded junk removal websites that load in under 2 seconds, convert mobile searches, and rank from day one.",
  intro:
    "Junk Removal websites lose the lead in the first 4 seconds on a phone. We hand-code fast, mobile-first junk removal sites with sticky CTAs, dedicated service pages, and conversion-tested intake flows. No bloated templates, no page builders — just a fast custom site that turns local searches into booked work.",
  definition: {
    term: "Junk Removal web design in Colorado",
    body: "Junk Removal web design is the practice of building conversion-focused, mobile-first websites for junk removal businesses — with dedicated service pages, clear CTAs, fast load times, and proper local SEO structure baked in from day one.",
  },
  whatWeDo: [
    { title: "Hand-coded for speed", body: "Sub-2-second mobile load with instant quote calculator above the fold." },
    { title: "Volume-based quote calculator", body: "Truck-load calculator that quotes and books in under 60 seconds." },
    { title: "Service pages per haul type", body: "Furniture, appliance, hot tub, mattress, construction, hoarding, estate — each ranks." },
    { title: "Same-day booking", body: "Real-time booking integration with ServiceMonster, Jobber, or your CRM." },
    { title: "Sticky click-to-call", body: "Phone visible on every scroll." },
    { title: "Schema and SEO at launch", body: "LocalBusiness, Service, FAQPage, Review schema baked in." },
  ],
  whyThisMarket: [
    { heading: "Same-day intent dominates", body: "Junk removal is a now-search. Same-day availability messaging above the fold wins the booking." },
    { heading: "Item-type pages outrank generic junk pages", body: "Hot tub removal, mattress haul-away, and appliance pickup are different searches with different pricing." },
    { heading: "Instant pricing beats form-fill", body: "Volume calculators close the booking before the competitor calls back." },
  ],
  relatedLinks: [
    { to: "/colorado-springs", label: "Colorado Springs digital marketing hub" },
    { to: "/colorado-springs-web-design", label: "How our web design process works" },
    { to: "/web-design-pricing", label: "Web design pricing" },
    { to: "/colorado-springs", label: "Colorado Springs digital marketing agency" },
    { to: "/web-design-denver-colorado", label: "Denver web design" },
    { to: "/case-studies", label: "Colorado case studies" },
    { to: "/web-design", label: "Our web design service (all industries)" },
    { to: "/pricing", label: "Web design pricing" },
  ],
  faqs: [
    { q: "How much does a junk removal website cost in Colorado?", a: "Website packages start at $1,850 one-time. All include one year of hosting and a built-in traffic dashboard. Extra pages are $85 each. Compare all packages or build a plan on our pricing page." },
    { q: "How long does it take to build a junk removal website?", a: "Typical timeline is 4–6 weeks for a 12–20 page junk removal site with full service and service-area pages. Larger multi-location junk removal businesses can take 8–10 weeks." },
    { q: "Will the new site rank from day one?", a: "Existing rankings stay or improve because we build proper 301 redirects from every old URL, keep your domain, and ship with full schema and on-page SEO from launch — not as a phase 2 afterthought." },
    { q: "Do you only serve Colorado Springs junk removal businesses?", a: "No — we build junk removal sites for businesses across all of Colorado: Denver, Fort Collins, Pueblo, Castle Rock, Parker, Boulder, and the Front Range." },
  ],
  serviceName: "Junk Removal Web Design in Colorado",
  serviceDescription:
    "Hand-coded, mobile-first websites for junk removal businesses across Colorado. Fast, conversion-focused, and SEO-ready from launch.",
  areaServed: "Colorado",
};

export const Route = createFileRoute("/junk-removal-web-design-colorado")({
  head: () => buildSpokeHead(CONFIG),
  component: () => <SpokePage config={CONFIG} />,
});
